// Added by Sejune Oh on 2020-03-18
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC, useEffect, useState, useMemo } from 'react'

// Helper
import LegacyEvtHandler from '@/helper/LegacyEvtHandler'
import Logger from '@/helper/Logger'
import {
  EVT_STUDYLIST_SELECTION,
  EVT_TASKLIST_INIT,
  EVT_TASKLIST_SELECTION,
  EVT_UPDATE_CURRENT_TASK_STATUS,
} from '@/helper/define/legacyEventDefine'
import { Task } from '@/helper/define/taskDefine'
import Intl from '@/helper/Intl'

// API
import { reqGetTasksByStudyUID } from '@/service/study/task.service'

// UI
import { TaskList } from '@/components/worklist/TaskList'

// Interface
import { WebComponentProps } from '@/helper/registerWebComponent'
import { TaskListStyleTypes } from './index'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const TaskListWrapper: FC<WebComponentProps> = ({
  customElementName,
  sendDataToCore,
  recvDataFromCore,
  classes,
  ...props
}) => {
  const dispDeletedRuleName = useMemo(() => Intl.getIntlText({ id: 'htmljs_react.worklist.tasklistwrapper.deleted_rule_name' }), [])

  // ! State
  const [tasks, setTasks] = useState<Task[]>([])
  const [curFkStudyId, setCurFkStudyId] = useState<string | null>(null)
  const [curProxyToken, setCurProxyToken] = useState<string | null>(null)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)

  useEffect(() => {
    /** ctrl + 클릭으로 Study 해제했을 때의 처리 */
    if (!recvDataFromCore) return
    if (!recvDataFromCore.data) {
      setTasks([])
      setSelectedTaskId(null)
      return
    }

    /** Tasklist on/off 시의 처리 */
    if (!curFkStudyId) {
      initializeTaskList(recvDataFromCore.data)
    }
  // eslint-disable-next-line
  }, [recvDataFromCore])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Initialize
  const initializeTaskList = async (data?: { dbId: string; selectedStudyId: string }) => {
    if (!data) {
      setTasks([])
      setSelectedTaskId(null)
      return
    }

    try {
      const { dbId: proxyToken, selectedStudyId: fkStudyId } = data
      if (!proxyToken || !fkStudyId) return
      
      const {
        data: { items },
      } = await reqGetTasksByStudyUID({ proxyToken, fkStudyId })

      setCurProxyToken(proxyToken)
      setCurFkStudyId(fkStudyId)

      const searchedTasks = items.map(({ rule_name, type, user_display_name, ...rest}) => {
        if (type === 'APS_RULE_TASK') {
          return {
            ...rest,
            type,
            rule_name: rule_name || dispDeletedRuleName,
            user_display_name: '*' + user_display_name
          }
        }

        return {
          ...rest,
          type,
          rule_name: rule_name || '-',
          user_display_name
        }
      })
      setTasks(searchedTasks)

      return searchedTasks
    } catch (e) {
      Logger.errLog('TASK_LIST.initializeTaskList : Fail to get task list')
      return
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Evt Handler
  const handleSelectTask = (taskId: string) => {
    setSelectedTaskId(taskId)

    LegacyEvtHandler.dispatchEvt(EVT_TASKLIST_SELECTION, {
      proxyToken: curProxyToken,
      fkStudyId: curFkStudyId,
      task: tasks.find(({ id }) => id === taskId),
    })
  }

  const handleSelectStudy = (event: any) => {
    try {
      const {
        detail: {
          dbId,
          focusedStudy: { id: selectedStudyId },
        },
      } = event
      LegacyEvtHandler.dispatchEvt(EVT_TASKLIST_INIT, {})

      if (dbId && selectedStudyId) {
        initializeTaskList({ dbId, selectedStudyId })
        setSelectedTaskId(null)
        return
      }
      initializeTaskList()
    } catch (e) {
      setCurFkStudyId(null)
    }
  }

  const handleTaskStatusChanged = (event: any) => {
    if (!curFkStudyId || !curProxyToken) {
      setTasks([])
      return
    }

    try {
      const {
        detail: { taskId, nextStatus, updateList },
      } = event

      if (updateList) {
        initializeTaskList({ dbId: curProxyToken, selectedStudyId: curFkStudyId })
        return
      } 

      setTasks([
        ...tasks.map((task) => {
          if (task.id !== taskId) return task

          return {
            ...task,
            status: nextStatus,
          }
        }),
      ])
    } catch (e) {
      setTasks([])
    }
  }

  const handleClickReload = async () => {
    if (!curProxyToken || !curFkStudyId) return
    const refreshedTasks = await initializeTaskList({ dbId: curProxyToken, selectedStudyId: curFkStudyId })

    if (!selectedTaskId) return
    LegacyEvtHandler.dispatchEvt(EVT_TASKLIST_SELECTION, {
      proxyToken: curProxyToken,
      fkStudyId: curFkStudyId,
      task: refreshedTasks!.find(({ id }) => id === selectedTaskId),
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Effects
  useEffect(() => {
    LegacyEvtHandler.addEvtListener(EVT_UPDATE_CURRENT_TASK_STATUS, handleTaskStatusChanged)
    LegacyEvtHandler.addEvtListener(EVT_STUDYLIST_SELECTION, handleSelectStudy)

    return () => {
      LegacyEvtHandler.removeEvtListener(EVT_UPDATE_CURRENT_TASK_STATUS, handleTaskStatusChanged)
      LegacyEvtHandler.removeEvtListener(EVT_STUDYLIST_SELECTION, handleSelectStudy)
    }

  // eslint-disable-next-line
  }, [curProxyToken, curFkStudyId, tasks])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Render
  return (
    <TaskList
      taskList={tasks}
      selectedTaskId={selectedTaskId || undefined}
      selectRowCallback={handleSelectTask}
      classes={classes as TaskListStyleTypes}
      onClickReload={handleClickReload}
    ></TaskList>
  )
}

export default TaskListWrapper
